/**
 * Merchant Fulfillment Page - Convert orders to packages
 * Allows merchants to create packages from paid orders and mark ready for pickup
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  Package,
  CheckCircle,
  Clock,
  Truck,
  QrCode,
  AlertCircle,
  RefreshCw,
  ChevronRight,
  Store,
  Box,
  Printer,
  Bell
} from 'lucide-react';

const MerchantFulfillment = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [packages, setPackages] = useState([]);
  const [processing, setProcessing] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch orders that need fulfillment (paid but no package yet)
      const [ordersRes, packagesRes] = await Promise.all([
        api.get('/orders/admin/all?status_filter=paid').catch(() => ({ data: [] })),
        api.get('/packages/merchant/pending').catch(() => ({ data: [] }))
      ]);
      
      setOrders(ordersRes.data || []);
      setPackages(packagesRes.data || []);
    } catch (error) {
      console.error('Failed to load fulfillment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Refreshed');
  };

  const createPackageFromOrder = async (orderId) => {
    setProcessing(orderId);
    try {
      const response = await api.post(`/scan/order/create-package?order_id=${orderId}&destination_type=DIRECT`);
      
      toast.success('Package created successfully!');
      
      // Refresh data
      await fetchData();
      
      // Show package info
      if (response.data?.tracking_code) {
        toast.info(`Tracking: ${response.data.tracking_code}`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create package';
      toast.error(errorMsg);
    } finally {
      setProcessing(null);
    }
  };

  const markReadyForPickup = async (packageId) => {
    setProcessing(packageId);
    try {
      await api.post(`/scan/package/${packageId}/ready`);
      
      toast.success('Package marked ready for pickup!');
      await fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update package';
      toast.error(errorMsg);
    } finally {
      setProcessing(null);
    }
  };

  // Print order/package details
  const handlePrintDetails = (item, type) => {
    const printContent = `
      <html>
        <head>
          <title>${type === "order" ? "Order" : "Package"} Details</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
            .section { margin-bottom: 15px; }
            .label { font-weight: bold; color: #666; }
            .total { font-size: 18px; font-weight: bold; margin-top: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${type === "order" ? "Order" : "Package"} #${item.id?.slice(0, 8) || item.tracking_code}</h1>
            ${item.tracking_code ? "<p>Tracking: " + item.tracking_code + "</p>" : ""}
          </div>
          <div class="section">
            <p class="label">Customer:</p>
            <p>${item.customer_name || item.recipient_name || "N/A"}</p>
          </div>
          <div class="section">
            <p class="label">Delivery Address:</p>
            <p>${item.delivery_address?.street || item.destination_address || ""}</p>
            <p>${item.delivery_address?.city || ""}</p>
          </div>
          <p style="margin-top: 40px; font-size: 12px; color: #999;">Printed: ${new Date().toLocaleString()}</p>
        </body>
      </html>
    `;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Print QR code shipping label
  const handlePrintQRLabel = (pkg) => {
    const qrDataUrl = "https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=" + encodeURIComponent(pkg.tracking_code || pkg.id);
    const labelContent = `
      <html>
        <head>
          <title>Shipping Label</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 400px; margin: 0 auto; }
            .label-box { border: 3px solid #000; padding: 20px; }
            .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 15px; }
            .qr-section { text-align: center; margin: 20px 0; }
            .qr-section img { width: 150px; height: 150px; }
            .tracking { font-size: 20px; font-weight: bold; text-align: center; letter-spacing: 2px; }
            .to { background: #f0f0f0; padding: 10px; margin-top: 20px; }
            .label { font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="label-box">
            <div class="header"><h2>LIANTIAO</h2><p style="font-size: 12px;">Jamaica Delivery</p></div>
            <div class="qr-section"><img src="${qrDataUrl}" alt="QR Code" /></div>
            <div class="tracking">${pkg.tracking_code || pkg.id?.slice(0, 8)}</div>
            <div class="to">
              <p class="label">DELIVER TO:</p>
              <p>${pkg.recipient_name || pkg.customer_name || "Customer"}</p>
              <p>${pkg.destination_address || ""}</p>
              ${pkg.pudo_name ? "<p style=\"color: #0066cc;\">PUDO: " + pkg.pudo_name + "</p>" : ""}
            </div>
          </div>
        </body>
      </html>
    `;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(labelContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="merchant-fulfillment-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/business')}
              className="p-2 hover:bg-gray-100 rounded-full"
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Box className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-800">Order Fulfillment</h1>
                <p className="text-xs text-gray-500">Create packages & mark ready</p>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2"
            data-testid="refresh-button"
          >
            <RefreshCw className={`h-5 w-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <span className="text-gray-600 text-sm">Pending Orders</span>
            </div>
            <p className="text-3xl font-bold text-gray-800" data-testid="pending-count">{orders.length}</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Package className="h-5 w-5 text-emerald-600" />
              </div>
              <span className="text-gray-600 text-sm">Ready Packages</span>
            </div>
            <p className="text-3xl font-bold text-gray-800" data-testid="ready-count">
              {packages.filter(p => p.status === 'READY_FOR_PICKUP').length}
            </p>
          </div>
        </div>

        {/* Orders to Fulfill */}
        <div className="space-y-3" data-testid="orders-section">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-500" />
            Orders to Package ({orders.length})
          </h2>
          
          {orders.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center border border-gray-100">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600">All orders have been packaged!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {orders.map((order) => (
                <div 
                  key={order.id}
                  className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
                  data-testid={`order-${order.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-800">
                          Order #{order.id?.slice(0, 8)}
                        </span>
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                          Paid
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {order.items?.length || 0} items • J${order.total_amount?.toLocaleString() || 0}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {order.delivery_address?.street}, {order.delivery_address?.city}
                      </p>
                    </div>
                    <Button
                      onClick={() => createPackageFromOrder(order.id)}
                      disabled={processing === order.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`create-package-${order.id}`}
                    >
                      {processing === order.id ? (
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        <>
                          <Package className="h-4 w-4 mr-2" />
                          Create Package
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Packages to Mark Ready */}
        <div className="space-y-3" data-testid="packages-section">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Package className="h-5 w-5 text-emerald-500" />
            Packages Created ({packages.length})
          </h2>
          
          {packages.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center border border-gray-100">
              <Box className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No packages pending</p>
            </div>
          ) : (
            <div className="space-y-2">
              {packages.map((pkg) => (
                <div 
                  key={pkg.id}
                  className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
                  data-testid={`package-${pkg.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-800">
                          {pkg.tracking_code || pkg.id?.slice(0, 8)}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          pkg.status === 'READY_FOR_PICKUP' 
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {pkg.status?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        To: {pkg.recipient_name || 'Customer'}
                      </p>
                      {pkg.package_qr_token && (
                        <div className="mt-2 flex items-center gap-2">
                          <QrCode className="h-4 w-4 text-gray-400" />
                          <span className="text-xs font-mono text-gray-400 truncate max-w-[150px]">
                            {pkg.package_qr_token?.slice(0, 20)}...
                          </span>
                        </div>
                      )}
                    </div>
                    {pkg.status === 'CREATED' && (
                      <Button
                        onClick={() => markReadyForPickup(pkg.id)}
                        disabled={processing === pkg.id}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid={`mark-ready-${pkg.id}`}
                      >
                        {processing === pkg.id ? (
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          <>
                            <Truck className="h-4 w-4 mr-2" />
                            Mark Ready
                          </>
                        )}
                      </Button>
                    )}
                    <div className="flex gap-2 mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePrintDetails(pkg, "package")}
                        title="Print Details"
                        data-testid={`print-${pkg.id}`}
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                      {pkg.tracking_code && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePrintQRLabel(pkg)}
                          title="Print QR Label"
                          data-testid={`qr-${pkg.id}`}
                        >
                          <QrCode className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {pkg.status === 'READY_FOR_PICKUP' && (
                      <div className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="h-5 w-5" />
                        <span className="text-sm font-medium">Ready</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <h3 className="font-semibold text-emerald-800 mb-2">Fulfillment Flow</h3>
          <ol className="text-sm text-emerald-700 space-y-1">
            <li>1. <strong>Create Package</strong> - Converts paid order to trackable package</li>
            <li>2. <strong>Pack Items</strong> - Prepare the physical package</li>
            <li>3. <strong>Mark Ready</strong> - Alerts drivers it's ready for pickup</li>
            <li>4. <strong>Driver Arrives</strong> - They'll scan your Package QR</li>
          </ol>
        </div>
      </main>
    </div>
  );
};

export default MerchantFulfillment;
